import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ErrorRetry from "@/components/ErrorRetry";
import { Loader2, Check, Smile } from "lucide-react";

const SCALES = [
  { key: "energy", label: "Energy" },
  { key: "sleep", label: "Sleep quality" },
  { key: "mood", label: "Mood" },
  { key: "overall", label: "Overall, how are you feeling?" },
];

function Scale({ label, value, onChange, testid }) {
  return (
    <div>
      <p className="text-sm font-medium mb-2">{label}</p>
      <div className="flex gap-2" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            data-testid={`${testid}-${n}`}
            className={`w-10 h-10 rounded-lg border text-sm font-semibold transition-all ${value === n ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-ring"}`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PatientCheckin() {
  const [form, setForm] = useState({ energy: 3, sleep: 3, mood: 3, overall: 3, side_effects: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyErr, setHistoryErr] = useState(null);
  const [saveErr, setSaveErr] = useState(null);

  /* Both catches here were empty. The submit one was the costly half: a check-in that
     failed to save showed the same green "Saved" tick as one that worked, cleared the
     form, and left the patient believing they had told their care team how they were
     feeling. That is the exact "things slipping between visits" failure this product
     exists to prevent, produced by the product itself. */
  const load = () => api.get("/patient/checkins")
    .then((r) => { setHistory(r.data); setHistoryErr(null); })
    .catch((e) => setHistoryErr(e));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveErr(null);
    try {
      await api.post("/patient/checkins", form);
      setDone(true);
      // Only reset the form once it is actually recorded. Clearing it on failure throws
      // away what they told us and shows them a blank form as if it had been filed.
      setForm({ energy: 3, sleep: 3, mood: 3, overall: 3, side_effects: "", notes: "" });
      load();
      setTimeout(() => setDone(false), 3000);
    } catch (e) {
      setSaveErr(e);
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="patient-checkin">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">How are you feeling?</h1>
        <p className="text-sm text-muted-foreground mt-1">A quick check-in helps your care team support you better.</p>
      </div>

      <ErrorRetry
        error={saveErr}
        variant="inline"
        onRetry={() => setSaveErr(null)}
        note="Your check-in was NOT saved and your care team has not seen it. Your answers are still on the form — please try again."
        testid="checkin-save-error"
      />
      <ErrorRetry
        error={historyErr}
        variant="inline"
        onRetry={() => { setHistoryErr(null); load(); }}
        note="Your earlier check-ins couldn't be loaded, so the history below may be incomplete."
        testid="checkin-history-error"
      />

      <form onSubmit={submit} className="bg-card border border-border rounded-xl p-6 space-y-5">
        {SCALES.map((s) => (
          <Scale key={s.key} label={s.label} value={form[s.key]} testid={`scale-${s.key}`} onChange={(n) => setForm({ ...form, [s.key]: n })} />
        ))}
        <div>
          <p className="text-sm font-medium mb-2">Any side effects?</p>
          <input data-testid="checkin-side-effects" value={form.side_effects} onChange={(e) => setForm({ ...form, side_effects: e.target.value })} placeholder="Optional" className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Anything else you'd like to share?</p>
          <textarea data-testid="checkin-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Optional" className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <button data-testid="submit-checkin" disabled={saving} className="w-full bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center justify-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : done ? <Check className="w-4 h-4" /> : <Smile className="w-4 h-4" />}
          {done ? "Thanks for checking in!" : "Submit check-in"}
        </button>
      </form>

      {history.length > 0 && (
        <div>
          <h3 className="font-heading text-base font-semibold mb-3">Recent check-ins</h3>
          <div className="space-y-2" data-testid="checkin-history">
            {history.slice(0, 5).map((c) => (
              <div key={c.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Feeling {c.overall}/5</p>
                  {c.notes && <p className="text-xs text-muted-foreground">{c.notes}</p>}
                </div>
                <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
